import { Component, EventEmitter, Inject, Input, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CpExamination } from '@se/models';
import {
    BoolIconRendererComponent,
    CpRouterDatabuilderService,
    EstensaWaitingService,
    fromServerIsoString,
    GridSelectionModel,
    IReportWorkflowExamService,
    REPORT_WORKFLOW_EXAM_SERVICE_TOKEN,
} from '@se/platform';
import { ButtonClick, ModalDialogNotificationService, NotificationType } from '@se/se.ui.html5';
import { RowEvent } from 'ag-grid-community';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { AddExamService, ExaminationsFilter } from './add-exam.service';

@Component({
    selector: 'add-exam',
    templateUrl: './add-exam.component.html',
    styleUrls: ['./add-exam.component.scss'],
})
export class AddExamComponent {
    @Input() selectedExamination!: CpExamination;
    @Input() showDialog!: boolean;
    @Output() closeDialog: EventEmitter<boolean> = new EventEmitter<boolean>();

    items: CpExamination[] = [];
    gridOptions: any;
    loading = false;
    message = '';
    examToSearch: ExaminationsFilter = {};

    private _exams: CpExamination[] = [];

    get exams(): CpExamination[] {
        return this._exams;
    }

    constructor(
        @Inject(REPORT_WORKFLOW_EXAM_SERVICE_TOKEN) private examService: IReportWorkflowExamService,
        private translate: TranslateService,
        public modalDialogNotificationService: ModalDialogNotificationService,
        private addExamService: AddExamService,
        private waitingService: EstensaWaitingService,
        private cpRouterDatabuilderService: CpRouterDatabuilderService,
    ) {
        this.gridOptions = {
            rowSelection: GridSelectionModel.single,
            suppressCellFocus: true,
            suppressLoadingOverlay: true,
            columnDefs: addExamService.buildColumns(),
            defaultColDef: { editable: false, sortable: true, resizable: true },
            enableBrowserTooltips: true,
            components: {
                boolIconRenderer: BoolIconRendererComponent,
            },
        };
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.showDialog && changes.showDialog.currentValue) {
            this.onOpen();
        }
    }

    async onAdd() {
        this.waitingService.isWaiting = true;
        const visitPatient = this.selectedExamination.patient; // riga selezionata delle visite
        const firstExam = this.exams[0]; // riga selezionata tra gli esami cercati
        const allExams = [...this.exams, this.selectedExamination];

        try {
            const cpArguments = await this.cpRouterDatabuilderService.buildCpArguments(allExams, (idReport) =>
                this.examService.getReportExaminations(idReport),
            );
            if (!cpArguments || !cpArguments.idCustomPageRoot) {
                return;
            }
        } finally {
            this.waitingService.isWaiting = false;
        }

        const name = visitPatient.firstName || visitPatient.name || '';
        const surname = visitPatient.lastName || visitPatient.surname || '';

        const visitPatientMsg =
            surname.toUpperCase() +
            ' ' +
            name.toUpperCase() +
            ' (' +
            fromServerIsoString(visitPatient.birthDate).toDate().toLocaleDateString() +
            ')';

        let buttonClick: ButtonClick = await firstValueFrom(
            this.modalDialogNotificationService.notify({
                buttonYes: true,
                buttonNo: true,
                buttonCancel: false,
                buttonOk: false,
                title: this.translate.instant('Warning'),
                message:
                    this.translate.instant('Visit_AddExamConfirm_1') +
                    ' ' +
                    firstExam.exam.description +
                    ' ' +
                    this.translate.instant('Visit_AddExamConfirm_2') +
                    ' ' +
                    visitPatientMsg +
                    ' ' +
                    this.translate.instant('Visit_AddExamConfirm_3'),
                type: NotificationType.warning,
            }),
        );
        if (buttonClick !== ButtonClick.yes) {
            return;
        }

        this.waitingService.isWaiting = true;
        const idExaminations = this.exams.map((e) => e.id);
        this.addExamService
            .addExam(this.selectedExamination.idReport || this.selectedExamination.idClinicalReport, idExaminations)
            .pipe(
                finalize(() => {
                    this.waitingService.isWaiting = false;
                    this.onClose(true);
                }),
            )
            .subscribe();
    }

    onClose(refreshReportList: boolean) {
        this.loading = false;
        this.message = '';
        this._exams = [];
        this.items = [];
        this.examToSearch = {};
        this.closeDialog.emit(refreshReportList);
    }

    onSearch() {
        this.loading = true;
        let searchFilter: ExaminationsFilter = {
            name: this.examToSearch.name,
            surname: this.examToSearch.surname,
            idPatient: this.examToSearch.idPatient,
        };
        if (this.examToSearch.date) {
            searchFilter.date = moment(this.examToSearch.date).utc().toDate();
        }
        this.addExamService.search(searchFilter).subscribe({
            next: (items) => {
                this.loading = false;
                this.items = items;
            },
            error: (exception) => {
                this.loading = false;
                this.message = this.lookupErrorMessage(exception);
                this._exams = [];
                this.items = [];
            },
        });
    }

    onResetDate() {
        this.examToSearch.date = null;
    }

    onSelectionChange(e: RowEvent) {
        if (e.api.getSelectedRows().length === 1) {
            this._exams[0] = e.api.getSelectedRows()[0];
        } else {
            this._exams = [];
        }
    }

    canAdd(): boolean {
        return this._exams?.length && !this.loading;
    }

    private lookupErrorMessage(exception) {
        if (exception && exception.error && exception.error.estensaFaultDetails) {
            if (exception.error.estensaFaultDetails.typeName === 'TooMuchResultFault') {
                return this.translate.instant(exception.error.estensaFaultDetails.typeName, {
                    p0: exception.error.estensaFaultDetails.estensaFault.count,
                    p1: exception.error.estensaFaultDetails.estensaFault.limit,
                    p2: ' ',
                });
            }
            return this.translate.instant('AssociateFailed') + ' ' + exception.message;
        }
    }

    private onOpen() {
        if (!this.selectedExamination) {
            return;
        }
        this.examToSearch = {
            name: this.selectedExamination.patient.firstName?.toUpperCase(),
            surname: this.selectedExamination.patient.lastName?.toUpperCase(),
            date: this.selectedExamination.examinationDate,
            idPatient: this.selectedExamination.patient.id,
        };
        this.onSearch();
    }
}
